import React, { useState } from "react";
import { DepartmentMaster, SiteType } from "../../../types";
import { X, ArrowLeft } from "lucide-react";

interface FilterMenuProps {
    sites: SiteType[];
    departments: DepartmentMaster[];
    applyFilters: (selectedSites: SiteType[], selectedDepartments: DepartmentMaster[]) => void;
    clearFilters: () => void;
    onClose: () => void;
}

const MobileFilterComponent: React.FC<FilterMenuProps> = ({ sites, departments, applyFilters, clearFilters, onClose }) => {
    const [currentMenu, setCurrentMenu] = useState<"main" | "sites" | "departments">("main");
    const [tempSelectedSites, setTempSelectedSites] = useState<SiteType[]>([]);
    const [tempSelectedDepartments, setTempSelectedDepartments] = useState<DepartmentMaster[]>([]);

    const handleBack = () => setCurrentMenu("main");

    const handleApplyFilters = () => {
        applyFilters(tempSelectedSites, tempSelectedDepartments);
        onClose();
    };

    const handleClearFilters = () => {
        setTempSelectedSites([]);
        setTempSelectedDepartments([]);
        clearFilters();
        onClose();
    };

    const toggleSelection = <T extends object>(item: T, selectedItems: T[], setSelectedItems: React.Dispatch<React.SetStateAction<T[]>>) => {
        setSelectedItems((prev) =>
            prev.some((i) => JSON.stringify(i) === JSON.stringify(item)) ? prev.filter((i) => JSON.stringify(i) !== JSON.stringify(item)) : [...prev, item]
        );
    };

    return (
        <div className="fixed inset-0 bg-white z-50 flex flex-col p-6">
            {/* Header with Close Button */}
            <div className="flex justify-between items-center mb-4">
                {currentMenu !== "main" && (
                    <button onClick={handleBack} className="p-2">
                        <ArrowLeft size={24} />
                    </button>
                )}
                <h2 className="text-lg font-bold flex-1 text-center">Filters</h2>
                <button onClick={onClose} className="p-2">
                    <X size={24} />
                </button>
            </div>

            {/* Main Menu */}
            {currentMenu === "main" && (
                <div className="flex flex-col gap-4">
                    <button className="w-full border border-gray-300 rounded-md px-4 py-3 text-left" onClick={() => setCurrentMenu("sites")}>
                        Sites
                    </button>
                    <button className="w-full border border-gray-300 rounded-md px-4 py-3 text-left" onClick={() => setCurrentMenu("departments")}>
                        Departments
                    </button>
                </div>
            )}

            {/* Sites Menu */}
            {currentMenu === "sites" && (
                <div className="flex flex-col">
                    {sites.map((site) => (
                        <div key={site.us_user_site_profile_id} className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                className="mr-2"
                                checked={tempSelectedSites.some((selectedSite) => JSON.stringify(selectedSite) === JSON.stringify(site))}
                                onChange={() => toggleSelection(site, tempSelectedSites, setTempSelectedSites)}
                            />
                            <label>{site.user_site_name || "Unnamed Site"}</label>
                        </div>
                    ))}
                </div>
            )}

            {/* Departments Menu */}
            {currentMenu === "departments" && (
                <div className="flex flex-col">
                    {departments.map((dept) => (
                        <div key={dept.department_code} className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                className="mr-2"
                                checked={tempSelectedDepartments.some((selectedDept) => JSON.stringify(selectedDept) === JSON.stringify(dept))}
                                onChange={() => toggleSelection(dept, tempSelectedDepartments, setTempSelectedDepartments)}
                            />
                            <label>{dept.department_name || "Unnamed Department"}</label>
                        </div>
                    ))}
                </div>
            )}

            {/* Apply & Clear Buttons */}
            <div className="mt-auto flex flex-col gap-2">
                <button className="w-full bg-blue-600 text-white px-4 py-3 rounded-md" onClick={handleApplyFilters}>
                    Apply Filters
                </button>
                <button className="w-full border border-gray-300 rounded-md px-4 py-3" onClick={handleClearFilters}>
                    Clear Filters
                </button>
            </div>
        </div>
    );
};

export default MobileFilterComponent;
